/** Workout history list, plus the edit and delete modals. */
import * as Sync from '../sync';
import { maybe, must } from '../dom';
import { byDate, escapeHtml, formatWorkout, getExerciseDisplayName } from '../lib/format';
import { currentExercise, currentWorkouts, metricsFor, saveLocal, workoutData } from '../lib/store';
import { updateCalendarView } from './calendar';
import { applyMetric, buildMetricField, readMetricInput } from './fields';
import { updateStats, updateProgressView } from './stats';
import type { Workout } from '../types';

let editingWorkoutId: string | null = null;

/**
 * Rows shown before "Show all". Ten is roughly one phone screen; everything
 * older stays one tap away, so nothing becomes impossible to edit or delete.
 */
const COLLAPSED_ROWS = 10;
let showAll = false;

export function displayWorkoutHistory(): void {
  const container = must('workout-history');
  const toggle = must<HTMLButtonElement>('history-toggle');
  const data = currentWorkouts();

  container.innerHTML = '';

  if (data.length === 0) {
    // Nothing to say here: the whole stats body is hidden when the device is
    // empty, and #stats-empty carries the message instead.
    toggle.hidden = true;
    return;
  }

  // Newest first. byDate is the single place chronology is defined, because
  // array order is not date order once a sync has appended remote records.
  const sorted = byDate(data).reverse();

  // "Expanded" only means something when there is more than fits. Resetting
  // here keeps a later tenth-plus workout from appearing already expanded.
  if (sorted.length <= COLLAPSED_ROWS) showAll = false;
  toggle.hidden = sorted.length <= COLLAPSED_ROWS;
  toggle.textContent = showAll ? 'Show less' : 'Show all';

  const visible = showAll ? sorted : sorted.slice(0, COLLAPSED_ROWS);

  for (const workout of visible) {
    const date = new Date(workout.date);
    const item = document.createElement('div');
    item.className = 'history-item';

    const dateEl = document.createElement('div');
    dateEl.className = 'date';
    dateEl.textContent = `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`;

    const repsEl = document.createElement('div');
    repsEl.className = 'reps';
    repsEl.textContent = formatWorkout(workout);

    const actions = document.createElement('div');
    actions.className = 'workout-actions';

    // Buttons carry the record id, not a list index: a sync can merge in
    // remote workouts between render and click, which shifts every index.
    for (const [action, icon, title, cls] of [
      ['edit', '✏️', 'Edit workout', 'edit-btn'],
      ['delete', '🗑️', 'Delete workout', 'delete-btn'],
    ] as const) {
      const button = document.createElement('button');
      button.className = cls;
      button.title = title;
      button.textContent = icon;
      button.dataset['action'] = action;
      button.dataset['workoutId'] = workout.id;
      actions.appendChild(button);
    }

    item.append(dateEl, repsEl, actions);
    container.appendChild(item);
  }
}

export function toggleHistoryExpansion(): void {
  showAll = !showAll;
  displayWorkoutHistory();
}

/**
 * Collapse again. Called when the exercise changes: an expanded push-ups
 * list should not carry over to pull-ups.
 */
export function resetHistoryExpansion(): void {
  showAll = false;
}

function findWorkout(id: string): { list: Workout[]; index: number } | null {
  const list = workoutData[currentExercise];
  if (!list) return null;
  const index = list.findIndex((w) => w.id === id);
  return index >= 0 ? { list, index } : null;
}

export function editWorkout(id: string): void {
  const found = findWorkout(id);
  const workout = found?.list[found.index];
  if (!found || !workout) return;

  editingWorkoutId = id;
  must('edit-exercise-display').textContent = getExerciseDisplayName(currentExercise);

  const container = must('edit-workout-inputs');
  container.innerHTML = '';
  const metrics = metricsFor(currentExercise);
  for (const metric of metrics) {
    container.appendChild(buildMetricField(metric, 'edit-', true));
  }
  for (const metric of metrics) {
    const field = maybe<HTMLInputElement>(`edit-${metric}-input`);
    if (field) field.value = workout[metric] === undefined ? '' : String(workout[metric]);
  }

  must('edit-workout-modal').style.display = 'block';
}

export function hideEditWorkoutModal(): void {
  must('edit-workout-modal').style.display = 'none';
  editingWorkoutId = null;
}

export function saveWorkoutEdit(): void {
  if (!editingWorkoutId) return;
  const found = findWorkout(editingWorkoutId);
  const existing = found?.list[found.index];
  if (!found || !existing) return;

  const updated: Workout = {
    id: existing.id,
    date: existing.date,
    exercise: currentExercise,
    clientUpdated: existing.clientUpdated,
  };

  let hasValidInput = false;
  for (const metric of metricsFor(currentExercise)) {
    const value = readMetricInput('edit-', metric);
    if (value === null) continue;
    applyMetric(updated, metric, value);
    hasValidInput = true;
  }

  if (!hasValidInput) {
    alert('Please enter valid values for at least one metric');
    return;
  }

  Sync.stampWorkout(updated);
  found.list[found.index] = updated;
  saveLocal();

  updateStats();
  displayWorkoutHistory();
  updateProgressView();
  hideEditWorkoutModal();
  alert('Workout updated successfully!');
}

export function deleteWorkout(id: string): void {
  const found = findWorkout(id);
  const workout = found?.list[found.index];
  if (!found || !workout) return;

  editingWorkoutId = id;
  const date = new Date(workout.date);

  must('delete-message').innerHTML =
    'Are you sure you want to delete this workout?<br><br>' +
    `<strong>${escapeHtml(formatWorkout(workout))}</strong><br>` +
    escapeHtml(
      `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
    );

  must('delete-workout-modal').style.display = 'block';
}

export function hideDeleteWorkoutModal(): void {
  must('delete-workout-modal').style.display = 'none';
  editingWorkoutId = null;
}

export function confirmDeleteWorkout(): void {
  if (!editingWorkoutId) return;
  const found = findWorkout(editingWorkoutId);
  if (!found) return;

  // Tombstone first: without it the record would simply sync back down.
  Sync.tombstoneWorkout(found.list[found.index]);
  found.list.splice(found.index, 1);
  saveLocal();

  updateStats();
  displayWorkoutHistory();
  updateProgressView();
  // Only the delete path touches the calendar: an edit keeps the original
  // date and exercise, so the day it falls on and the count there cannot move.
  updateCalendarView();
  hideDeleteWorkoutModal();
  alert('Workout deleted successfully!');
}
